/**
 * MaisonPdfExportButton — V14.63 Sprint 27.
 *
 * Single-button orchestrator that :
 *   1. Mounts an offscreen Exterior3DViewer + Shoebox3DViewer per floor
 *      inside a hidden <div> so each viewer renders and we can grab a PNG.
 *   2. Calls renderer.domElement.toDataURL('image/png') on each canvas
 *      via a ref handed back through `onReady` callbacks.
 *   3. POSTs the consolidated payload (exterior_b64 + floor captures +
 *      micro-IFC counts) to the right backend endpoint and triggers a
 *      PDF download.
 *
 * Two flows :
 *   • Standalone maison (sh_*)  → POST /api/single-house/projects/{id}/export-pdf
 *   • V2 maison (proj_*)        → POST /api/projects/{id}/maison/export-pdf
 *
 * The component is intentionally heavy (it mounts every viewer at full
 * resolution then unmounts after capture) but only when the user clicks
 * the button. Idle cost = zero.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Loader2 } from 'lucide-react';
import Shoebox3DViewer from './Shoebox3DViewer';
import Exterior3DViewer from './Exterior3DViewer';
import BuildingStack3DViewer from './BuildingStack3DViewer';
import { formatApiError } from '../../lib/formatApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAPTURE_W = 1280;
const CAPTURE_H = 720;
// Wait for three.js to render at least one frame + lighting / textures.
const CAPTURE_DELAY_MS = 700;

const grabCanvasDataUrl = (container) => {
  if (!container) return null;
  const canvas = container.querySelector('canvas');
  if (!canvas) return null;
  try {
    // JPEG compressé + taille plafonnée : les PNG plein écran multi-étages
    // dépassaient la limite de 32 MB des requêtes Cloud Run (rejet HTTP sans
    // en-têtes CORS → faux "Network Error" côté navigateur).
    const MAX_DIM = 1600;
    const scale = Math.min(1, MAX_DIM / Math.max(canvas.width, canvas.height));
    const out = document.createElement('canvas');
    out.width = Math.round(canvas.width * scale);
    out.height = Math.round(canvas.height * scale);
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff';               // JPEG sans alpha : fond blanc
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, 0, out.width, out.height);
    return out.toDataURL('image/jpeg', 0.85);
  } catch (e) {
    return null;
  }
};

const MaisonPdfExportButton = ({
  // Standalone or V2 — exactly one of these must be set.
  houseId,
  projectId,
  // Cover metadata
  title,
  address,
  subtitle,
  // The full list of floors (with geometry) used by the 3D viewers.
  floors,
  // The exterior payload used by Exterior3DViewer (already fetched by parent).
  exteriorPayload,
  // Misc
  className = '',
  testId = 'maison-pdf-export-btn',
}) => {
  const [exporting, setExporting] = useState(false);
  const [phase, setPhase] = useState(null);  // 'capturing-exterior' | 'capturing-floors' | 'building-pdf'
  // Hidden captures container refs.
  const exteriorBoxRef = useRef(null);
  const floorBoxRefs = useRef({});  // floor_id -> div ref
  const axoBoxRef = useRef(null); // Sprint 34 — exploded axo capture
  const captureSettlerRef = useRef(null);  // promise resolver for current phase

  // Resolve captures once mounted.
  const settle = useCallback((data) => {
    if (captureSettlerRef.current) {
      captureSettlerRef.current(data);
      captureSettlerRef.current = null;
    }
  }, []);

  // Mount/unmount of the hidden viewers is gated by `exporting`. When the
  // viewers render we wait CAPTURE_DELAY_MS, grab their canvas, then unmount.
  const [stage, setStage] = useState(null);  // null | 'exterior' | 'floors'
  const [floorIndex, setFloorIndex] = useState(0);
  const floorsWithWalls = (floors || []).filter(
    (f) => (f.geometry?.walls?.length || 0) > 0);

  useEffect(() => {
    if (stage !== 'exterior') return undefined;
    const t = setTimeout(() => {
      const dataUrl = grabCanvasDataUrl(exteriorBoxRef.current);
      settle({ exterior_b64: dataUrl });
    }, CAPTURE_DELAY_MS);
    return () => clearTimeout(t);
  }, [stage, settle]);

  useEffect(() => {
    if (stage !== 'floors') return undefined;
    const current = floorsWithWalls[floorIndex];
    if (!current) return undefined;
    const t = setTimeout(() => {
      const container = floorBoxRefs.current[current.floor_id];
      const dataUrl = grabCanvasDataUrl(container);
      settle({ floor_id: current.floor_id, shoebox_b64: dataUrl });
    }, CAPTURE_DELAY_MS);
    return () => clearTimeout(t);
  }, [stage, floorIndex, floorsWithWalls, settle]);

  useEffect(() => {
    if (stage !== 'axo') return undefined;
    const t = setTimeout(() => {
      const dataUrl = grabCanvasDataUrl(axoBoxRef.current);
      settle({ axo_b64: dataUrl });
    }, CAPTURE_DELAY_MS + 400); // extra time for stack layout + exploded shifts
    return () => clearTimeout(t);
  }, [stage, settle]);

  const captureExterior = () => new Promise((resolve) => {
    captureSettlerRef.current = resolve;
    setStage('exterior');
  });

  const captureFloor = (idx) => new Promise((resolve) => {
    captureSettlerRef.current = resolve;
    setFloorIndex(idx);
    setStage('floors');
  });

  const captureAxo = () => new Promise((resolve) => {
    captureSettlerRef.current = resolve;
    setStage('axo');
  });

  const runExport = async () => {
    if (exporting) return;
    if (!houseId && !projectId) {
      toast.error('Aucun projet sélectionné');
      return;
    }
    setExporting(true);
    try {
      // 1) Capture exterior
      setPhase('capturing-exterior');
      const ext = exteriorPayload ? await captureExterior() : { exterior_b64: null };

      // 2) Capture each floor that has walls
      setPhase('capturing-floors');
      const floorCaptures = [];
      for (let i = 0; i < floorsWithWalls.length; i += 1) {
        const f = floorsWithWalls[i];
        const cap = await captureFloor(i);
        floorCaptures.push({
          floor_id: f.floor_id,
          name: f.name || '',
          code: f.code || '',
          level: Number.isFinite(f.level) ? f.level : 0,
          height_m: Number.isFinite(f.height_m) ? f.height_m : 0,
          walls: (f.geometry?.walls?.length || 0),
          windows: (f.geometry?.windows?.length || 0),
          doors: (f.geometry?.doors?.length || 0),
          stairs: (f.geometry?.stairs?.length || 0),
          terrain_vertices: (f.geometry?.terrain?.length || 0),
          scale_m_per_px: Number.isFinite(f.geometry?.scale_m_per_px)
            ? f.geometry.scale_m_per_px : 0,
          shoebox_b64: cap.shoebox_b64 || null,
        });
      }

      // 3) Sprint 34 — Capture axonometric exploded view (if multiple floors)
      let axoB64 = null;
      if (floorsWithWalls.length >= 2) {
        setPhase('capturing-axo');
        const axo = await captureAxo();
        axoB64 = axo.axo_b64 || null;
      }

      // 4) POST to the proper endpoint and download
      setPhase('building-pdf');
      const url = houseId
        ? `${API}/single-house/projects/${houseId}/export-pdf`
        : `${API}/projects/${projectId}/maison/export-pdf`;
      const { data } = await axios.post(url, {
        exterior_b64: ext.exterior_b64,
        axo_b64: axoB64,
        floors: floorCaptures,
        title: title || '',
        address: address || '',
        subtitle: subtitle || '',
      }, {
        withCredentials: true,
        responseType: 'blob',
      });
      // Trigger download
      const blobUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `Dossier_Maison_${(title || 'maison').replace(/[^A-Za-z0-9-]+/g, '_')}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      toast.success('Dossier PDF généré', {
        description: `${floorCaptures.length} étage(s) inclus${ext.exterior_b64 ? ' + vue extérieure' : ''}`,
      });
    } catch (err) {
      toast.error(formatApiError(err, 'Export PDF échoué'));
    } finally {
      setStage(null);
      setFloorIndex(0);
      setPhase(null);
      setExporting(false);
    }
  };

  // Hidden 3D capture viewers (only mounted while exporting).
  const renderHiddenCaptures = () => (
    <div
      style={{
        position: 'fixed', left: -99999, top: -99999,
        width: CAPTURE_W, height: CAPTURE_H,
        pointerEvents: 'none', opacity: 0.001,
      }}
      aria-hidden="true"
    >
      {stage === 'exterior' && exteriorPayload && (
        <div ref={exteriorBoxRef}
             style={{ width: CAPTURE_W, height: CAPTURE_H }}>
          <Exterior3DViewer
            exteriorPayload={exteriorPayload}
            title={title || 'Maison'}
            subtitle={subtitle || ''}
            className="w-full h-full"
            testId="pdf-capture-exterior"
          />
        </div>
      )}
      {stage === 'floors' && floorsWithWalls[floorIndex] && (
        <div
          ref={(el) => {
            const f = floorsWithWalls[floorIndex];
            if (f && el) floorBoxRefs.current[f.floor_id] = el;
          }}
          style={{ width: CAPTURE_W, height: CAPTURE_H }}
        >
          <Shoebox3DViewer
            floor={floorsWithWalls[floorIndex]}
            height={floorsWithWalls[floorIndex]?.height_m}
            showGrid
            showTerrain
            className="w-full h-full"
            testId={`pdf-capture-floor-${floorsWithWalls[floorIndex].code || floorIndex}`}
          />
        </div>
      )}
      {stage === 'axo' && (
        <div ref={axoBoxRef}
             style={{ width: CAPTURE_W, height: CAPTURE_H }}>
          <BuildingStack3DViewer
            floors={floorsWithWalls}
            className="w-full h-full"
            testId="pdf-capture-axo"
            explodedGap={3}
          />
        </div>
      )}
    </div>
  );

  const phaseLabel = {
    'capturing-exterior': 'Capture vue extérieure…',
    'capturing-floors':   `Capture étage ${floorIndex + 1}/${floorsWithWalls.length}…`,
    'capturing-axo':      'Capture vue axonométrique éclatée…',
    'building-pdf':       'Génération du PDF…',
  }[phase] || 'Exporter le dossier PDF';

  return (
    <>
      <button
        type="button"
        onClick={runExport}
        disabled={exporting || (!floors?.length && !exteriorPayload)}
        data-testid={testId}
        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded text-[11px] font-semibold border border-emerald-400/70 bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
        title="Génère un dossier PDF avec la vue extérieure 3D et une Shoebox 3D par étage."
      >
        {exporting
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <FileText className="w-3.5 h-3.5" />}
        {phaseLabel}
      </button>
      {exporting && renderHiddenCaptures()}
    </>
  );
};

export default MaisonPdfExportButton;
